import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const deleteCategorySkill: Skill = {
  id: "deleteCategory",
  description:
    "Elimina una categoría del menú. Si tiene productos asignados, AUTOMÁTICAMENTE los mueve a 'sin categoría' antes de eliminar. Acción IRREVERSIBLE — la categoría desaparece pero los productos permanecen (sin categoría asignada).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      categoryId: { type: "string" },
    },
    required: ["restaurantId", "categoryId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const categoryId = String(args.categoryId ?? "");
    if (!restaurantId || !categoryId)
      return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar categoría",
      description:
        "Si la categoría tiene productos, se moverán automáticamente a 'sin categoría'. La categoría se elimina permanentemente.",
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Eliminando categoría…",
      successMsg: "✓ Categoría eliminada",
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          const { data } = await apiWithAuth.delete(
            `/restaurants/${restaurantId}/product-categories/${categoryId}`,
            ctx.accessToken,
          );
          emitCopilotMutation({
            scope: "categories",
            action: "deleted",
            restaurantId,
            id: categoryId,
          });
          emitCopilotMutation({
            scope: "products",
            action: "updated",
            restaurantId,
          });
          return {
            ok: true as const,
            result: {
              categoryId,
              productsMoved: data?.productsMoved ?? 0,
            },
            humanSummary:
              data?.productsMoved > 0
                ? `Categoría "${data.categoryName}" eliminada. ${data.productsMoved} producto(s) movidos a "sin categoría".`
                : `Categoría "${data?.categoryName ?? ""}" eliminada (estaba vacía).`,
          };
        } catch (err: any) {
          return {
            ok: false as const,
            error:
              err?.response?.data?.message ?? "Error eliminando categoría.",
          };
        }
      },
    });
  },
};

export const createCategorySkill: Skill = {
  id: "createCategory",
  description:
    "Crea una nueva categoría en el menú del restaurante (ej. 'Entradas', 'Postres'). Útil cuando el usuario quiere organizar productos o agregar un grupo nuevo.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string", description: "Nombre de la categoría" },
    },
    required: ["restaurantId", "name"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    if (!restaurantId || !name) return { ok: false, error: "Faltan datos." };

    // Anti-duplicado: si ya existe una categoría con ese nombre (case-insensitive),
    // devolvemos la existente sin crear una nueva.
    try {
      const { data: existing } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/product-categories`,
        ctx.accessToken,
      );
      const items: any[] = existing?.items ?? existing?.data ?? existing ?? [];
      const dup = items.find(
        (c) => String(c.name).trim().toLowerCase() === name.toLowerCase(),
      );
      if (dup) {
        return {
          ok: true,
          result: { id: dup.id, name: dup.name, alreadyExisted: true },
          humanSummary: `La categoría "${dup.name}" ya existía — uso esa.`,
        };
      }
    } catch {
      // Si falla la pre-check, intentamos crear igualmente
    }

    const ok = await ctx.confirmation.confirm({
      title: "Crear categoría",
      details: [{ label: "Nombre", value: name }],
      confirmLabel: "Crear",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando categoría "${name}"…`,
      successMsg: `✓ Categoría "${name}" creada`,
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/product-categories`,
            { name },
            ctx.accessToken,
          );
          emitCopilotMutation({
            scope: "categories",
            action: "created",
            restaurantId,
            id: data?.id,
            meta: { name },
          });
          return {
            ok: true as const,
            result: { id: data?.id, name },
            humanSummary: `Categoría "${name}" creada.`,
          };
        } catch (err: any) {
          return {
            ok: false as const,
            error: err?.response?.data?.message ?? "Error creando categoría.",
          };
        }
      },
    });
  },
};

export const updateCategorySkill: Skill = {
  id: "updateCategory",
  description:
    "Renombra o actualiza una categoría existente. Si solo conoces el nombre actual, primero usa listCategories.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      categoryId: { type: "string" },
      name: { type: "string", description: "Nuevo nombre" },
      isActive: { type: "boolean" },
    },
    required: ["restaurantId", "categoryId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const categoryId = String(args.categoryId ?? "");
    if (!restaurantId || !categoryId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) {
      patch.name = String(args.name);
      details.push({ label: "Nuevo nombre", value: patch.name as string });
    }
    if (typeof args.isActive === "boolean") {
      patch.isActive = args.isActive;
      details.push({ label: "Activa", value: args.isActive ? "Sí" : "No" });
    }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({
      title: "Actualizar categoría",
      details,
      confirmLabel: "Actualizar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando categoría…",
      successMsg: "✓ Categoría actualizada",
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          const { data } = await apiWithAuth.patch(
            `/restaurants/${restaurantId}/product-categories/${categoryId}`,
            patch,
            ctx.accessToken,
          );
          emitCopilotMutation({
            scope: "categories",
            action: "updated",
            restaurantId,
            id: categoryId,
          });
          return {
            ok: true as const,
            result: { id: data?.id ?? categoryId, ...patch },
            humanSummary: "Categoría actualizada.",
          };
        } catch (err: any) {
          return {
            ok: false as const,
            error: err?.response?.data?.message ?? "Error actualizando categoría.",
          };
        }
      },
    });
  },
};
